import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import FormTextArea from 'components/form/FormTextArea';
import { useFormContext } from 'react-hook-form';
import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';
import { getDivSelectOptions } from 'utils/app';

import type { JSX } from 'react';

/**
 * ボイスチャット作成編集 🎙️ボイスチャット設定コンポーネント（モック VcVoiceChatEdit.tsx 468-570 行相当）。
 */
export default function WsVoiceChatEditSettings(props: { readOnly: boolean }): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<VoiceChatEditFormStateType>();

  const guidanceFlag = watch('guidanceFlag');

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <section className="section-form-default">
      <div className="container">
        <div className="box-common shadow-none">
          <h3 className="title-section title-md">🎙️ ボイスチャット設定</h3>
          <div className="form-default-container">
            {/* 利用規約の同意取得 */}
            <div className="form-group">
              <div className="form-check">
                <input
                  id="consentFlag"
                  type="checkbox"
                  className="form-check-input"
                  disabled={props.readOnly}
                  {...register('consentFlag')}
                />
                <label className="form-check-label" htmlFor="consentFlag">
                  開始前に利用規約・プライバシーポリシーへの同意を取得する
                </label>
              </div>
            </div>

            {/* マイク許可（固定ON・保存なし） */}
            <div className="form-group">
              <div className="form-check">
                <input
                  id="voiceChatMic"
                  type="checkbox"
                  className="form-check-input"
                  checked
                  disabled
                />
                <label className="form-check-label" htmlFor="voiceChatMic">
                  開始時にマイクの利用許可を取得する（音声回答に必要なため常にオン）
                </label>
              </div>
            </div>

            {/* 開始ガイダンス */}
            <div className="form-group">
              <div className="form-check">
                <input
                  id="guidanceFlag"
                  type="checkbox"
                  className="form-check-input"
                  disabled={props.readOnly}
                  {...register('guidanceFlag')}
                />
                <label className="form-check-label" htmlFor="guidanceFlag">
                  開始時に音声ガイダンスを再生する
                </label>
              </div>
            </div>

            {/* 多言語対応 */}
            <div className="form-group">
              <div className="form-check">
                <input
                  id="multilingualFlag"
                  type="checkbox"
                  className="form-check-input"
                  disabled={props.readOnly}
                  {...register('multilingualFlag')}
                />
                <label className="form-check-label" htmlFor="multilingualFlag">
                  多言語対応（公開時に英・中・韓へ自動翻訳し各言語の音声を生成。ユーザのブラウザの言語設定に応じて表示言語を自動で切り替え）
                </label>
              </div>
            </div>

            {/* パラメータモード（視聴トラッキング） */}
            <div className="form-group">
              <div className="form-check">
                <input
                  id="trackingParamFlag"
                  type="checkbox"
                  className="form-check-input"
                  disabled={props.readOnly}
                  {...register('trackingParamFlag')}
                />
                <label className="form-check-label" htmlFor="trackingParamFlag">
                  パラメータモード（受診者ごとの視聴トラッキングを有効化）
                </label>
              </div>
            </div>
            {guidanceFlag && (
              <>
                <div className="form-group">
                  <div className="form-check">
                    <input
                      id="guidanceTextDisplayFlag"
                      type="checkbox"
                      className="form-check-input"
                      disabled={props.readOnly}
                      {...register('guidanceTextDisplayFlag')}
                    />
                    <label className="form-check-label" htmlFor="guidanceTextDisplayFlag">
                      開始ガイダンスの文言を画面に表示する
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <FormTextArea
                    subject="開始ガイダンスの文言"
                    name="guidanceText"
                    rows={2}
                    register={register}
                    errors={errors}
                    readOnly={props.readOnly}
                  />
                </div>
                <div className="form-group">
                  <FormText
                    subject="読み上げ用テキスト（任意）"
                    name="guidanceSpeechText"
                    placeholder="誤読する漢字をひらがなにして入力"
                    register={register}
                    errors={errors}
                    readOnly={props.readOnly}
                  />
                  <p className="vc-hint" style={{ marginTop: 6 }}>
                    空のときはガイダンスの文言をそのまま読み上げます。
                  </p>
                </div>
              </>
            )}

            {/* 読み上げ音声 */}
            <div className="form-group">
              <FormSelect
                subject="読み上げ音声"
                name="voiceDiv"
                register={register}
                errors={errors}
                options={getDivSelectOptions('voiceChatVoice')}
                disabled={props.readOnly}
              />
            </div>

            {/* 回答確定ボタン表示（guidanceFlag 条件に従属しない・無条件表示） */}
            <div className="form-group">
              <div className="form-check">
                <input
                  id="answerConfirmButtonFlag"
                  type="checkbox"
                  className="form-check-input"
                  disabled={props.readOnly}
                  {...register('answerConfirmButtonFlag')}
                />
                <label className="form-check-label" htmlFor="answerConfirmButtonFlag">
                  回答確定ボタンを表示する（オフにすると録音後に自動で次の設問へ進む）
                </label>
              </div>
            </div>

            {/* 説明後の自動録音開始（guidanceFlag 条件に従属しない・無条件表示） */}
            <div className="form-group">
              <div className="form-check">
                <input
                  id="answerAutoStartFlag"
                  type="checkbox"
                  className="form-check-input"
                  disabled={props.readOnly}
                  {...register('answerAutoStartFlag')}
                />
                <label className="form-check-label" htmlFor="answerAutoStartFlag">
                  説明後に自動で録音を開始する（オフにすると「回答する」ボタンを押してから録音）
                </label>
              </div>
            </div>

            {/* 無音検知 / 最大録音時間（モックは自由入力の number input） */}
            <div className="row align-items-end">
              <div className="col-md form-group">
                <FormText
                  subject="無音と判定するまでの待ち時間（秒）"
                  name="silenceWaitSec"
                  format="numeric"
                  register={register}
                  errors={errors}
                  readOnly={props.readOnly}
                />
              </div>
              <div className="col-md form-group">
                <FormText
                  subject="1問あたりの最大録音時間（秒）"
                  name="maxRecordSec"
                  format="numeric"
                  register={register}
                  errors={errors}
                  readOnly={props.readOnly}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
